import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { MapView, MapViewMarker } from "@msuite/ui/map";
import { Popover } from "@msuite/ui/popover";
import { getGeometryCoordinates } from "@msuite/utils/misc";
import { randomNumber } from "@msuite/utils/numbers";
import { guardString } from "@msuite/utils/strings";
import type { Nullable, Tables } from "@msuite/utils/types";
import { ProjectPopover } from "@/components/popovers/project-popover";

/** Props Interface */
interface ProjectMapProps {
	project: Nullable<Tables<"projects">>;
}

export function ProjectMap({ project }: ProjectMapProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<ContentCard
			title={t("maps.map")}
			icon={icon("Map")}
			className="flex-1 mb-4"
			contentClassName="h-full !p-0 min-h-[400px]"
		>
			<MapView
				markers={getProjectMarkers(project)}
				apiKey={guardString(process.env.PUBLIC_GOOGLE_MAPS_API_KEY)}
				mapId={process.env.PUBLIC_GOOGLE_MAPS_MAP_ID!}
			/>
		</ContentCard>
	);
}

function getProjectMarkers(
	project: Nullable<Tables<"projects">>,
): { latitude: number; longitude: number; id: string }[] {
	if (!project?.geometry) return [];
	const coords = getGeometryCoordinates(project.geometry);
	if (!coords.latitude || !coords.longitude) return [];
	return [
		{
			id: project.id,
			latitude: coords.latitude,
			longitude: coords.longitude,
		},
	];
}

const latitude = randomNumber(52.2, 52.8, false);
const longitude = randomNumber(12.8, 14.0, false);

function _createSample(projectId: string) {
	return {
		id: projectId,
		latitude,
		longitude,
		render: () => (
			<Popover
				trigger="click"
				variant="plain"
				render={() => <ProjectPopover projectId={projectId} />}
			>
				<MapViewMarker
					iconName="FolderOpen"
					color="var(--color-red-500)"
				/>
			</Popover>
		),
	};
}
